import { useState, useEffect, useMemo } from 'react';
import {
  TabSelector,
  TextInput,
  Checkbox,
  Button,
  Divider,
  Badge,
} from '@burna/monster-design-system';
import { Link, useHistory } from 'react-router-dom';
import axios from 'axios';
import MetaMaskOnboarding from '@metamask/onboarding';
import { APIName } from 'config';
import useUser from 'hooks/useUser';
import useAlertWithTimeout from 'hooks/useAlertWithTimeout';
import styles from './index.module.scss';
import Captcha from 'components/captcha';

function Login() {
  const user = useUser();
  const alertWithTimeout = useAlertWithTimeout();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberChecked, setRememberChecked] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [isSubmitted, setSubmitted] = useState(false);
  const history = useHistory();
  const [{ error, message }, setStatus] = useState({});
  const [isDisabled, setDisabled] = useState(false);
  const [isMobileDevice, setMobileDevice] = useState(false);
  const onboarding = new MetaMaskOnboarding();
  const [captchaCode, setCaptchaCode] = useState('');
  const [refreshCaptcha, setRefreshCaptcha] = useState(null);

  const usernameErrors = useMemo(() => {
    if (!username) {
      return ['Enter username'];
    }
    return [];
  }, [username]);

  const passwordErrors = useMemo(() => {
    if (!password) {
      return ['Enter password'];
    }
    const errors = [];
    if (password.length < 8) {
      errors.push('Must be at least 8 characters');
    }
    if (!/[a-zA-Z]/.test(password) || !/\d/.test(password)) {
      errors.push('Must be a combination of numbers and letters');
    }
    return errors;
  }, [password]);

  useEffect(async () => {
    detectMob();
    if (user.username) {
      history.push('/fileList');
    }
  }, []);

  const handleSubmit = event => {
    setStatus({
      error: false,
      message: ''
    });
    event.preventDefault();
    setSubmitted(true);
    if (!username || !password || !captchaCode) {
      return;
    }
    setSubmitting(true);
    axios
      .post(`/api/${APIName}/verify-captcha`, {
        username: username,
        captcha: captchaCode
      })
      .then(res => {
        axios
          .post(`/api/${APIName}/loginUser`, {
            username: username,
            password: password,
            remember: rememberChecked,
          })
          .then(res => {
            setStatus({
              error: false,
              message: 'successfully login'
            });
            user.setUser({ username }, rememberChecked);
            history.replace('/fileList');
            alertWithTimeout('Welcome', 'Welcome to your zDrive panel!', 3000);
          })
          .catch(err => {
            setCaptchaCode('');
            setRefreshCaptcha(Date.now());
            setSubmitting(false);
            if (err?.response?.status === 401) {
              setStatus({
                error: true,
                message: 'Login failed. check your username and password.'
              });
            } else {
              setStatus({
                error: true,
                message: 'Error connecting to server'
              });
            }
          });
      })
      .catch(err => {
        setCaptchaCode('');
        setRefreshCaptcha(Date.now());
        setSubmitting(false);
        if (err?.response?.status === 401) {
          let errorMessage = err?.response?.data?.message || '';
          setStatus({
            error: true,
            message: `${errorMessage} Please try again.`
          });
        } else {
          setStatus({
            error: true,
            message: 'Error connecting to server'
          });
        }
      });
  };

  const handleUsername = event => {
    setUsername(event.target.value);
  };

  const handlePassword = event => {
    setPassword(event.target.value);
  };

  const detectMob = () => {
    let details = navigator.userAgent;
    let regexp =
      /android|iphone|kindle|ipad|Windows Phone|Tablet|Mobile|MOTO|KFAPWI/i;
    let isMobile = regexp.test(details);
    setMobileDevice(isMobile);
  };

  const handleLoginWithWeb3 = async () => {
    setStatus({
      error: false,
      message: ''
    });
    const { ethereum } = window;
    if (ethereum) {
      setDisabled(true);
      setSubmitting(true);
      const reqMethod = isMobileDevice
        ? 'eth_requestAccounts'
        : 'wallet_requestPermissions';
      const paramsMethod = isMobileDevice ? [] : [{ eth_accounts: {} }];
      await ethereum
        .request({
          method: reqMethod,
          params: paramsMethod
        })
        .then(response => {
          const account = isMobileDevice
            ? response[0]
            : response[0].caveats[0].value[0];
          setStatus({
            error: false,
            message: 'successfully login'
          });
          user.setUser(
            {
              username,
              type: 'web3'
            },
            rememberChecked
          );
          history.push('/fileList');
        })
        .catch(err => {
          setDisabled(false);
          setSubmitting(false);
          setStatus({
            error: true,
            message:
              err.code === 4001
                ? 'The wallet was closed before connecting.'
                : 'A connection error has occurred.'
          });
        });
    } else if (isMobileDevice) {
      setStatus({
        error: true,
        message: 'Install Metamask application, then try again.'
      });
      const dappUrl = window.location.host;
      const metamaskAppDeepLink = 'https://metamask.app.link/dapp/' + dappUrl;
      window.open(metamaskAppDeepLink);
    } else {
      setStatus({
        error: true,
        message: 'Install Metamask extension, then refresh the page.'
      });
      onboarding._openDownloadPage();
    }
  };

  return (
    <div className={styles.container}>
      <TabSelector className={styles.tabSelector} selectedIndex={0}>
        <TabSelector.Tab as={Link} to="/login" icon="log-in" text="login" />
        <TabSelector.Tab
          as={Link}
          to="/register"
          icon="user-plus"
          text="register"
        />
      </TabSelector>
      <div className={styles.title}>
        Welcome back!
        <div className={styles.subtitle}>Please enter your details.</div>
      </div>
      {error && (
        <Badge
          className={styles.badge}
          size="sm"
          type="error"
          icon="alert-circle"
          label={message}
        />
      )}
      <form
        className={styles.form}
        onSubmit={handleSubmit}
        disabled={isSubmitting}>
        <TextInput
          className={styles.textInput}
          title="Username"
          icon="user"
          placeholder="Enter your username/phone number"
          value={username}
          onChange={handleUsername}
          disabled={isSubmitting}
          errorMessages={usernameErrors}
          hasError={isSubmitted && usernameErrors.length > 0}
        />
        <TextInput
          className={styles.textInput}
          type="password"
          title="Password"
          icon="lock"
          placeholder="Enter your password"
          value={password}
          onChange={handlePassword}
          disabled={isSubmitting}
          errorMessages={passwordErrors}
          hasError={isSubmitted && passwordErrors.length > 0}
        />
        <Captcha
          refreshCaptcha={refreshCaptcha}
          onCaptchaChange={setCaptchaCode}
          isSubmitted={isSubmitted}
          disabled={isSubmitting}
        />
        <div className={styles.remember}>
          <Checkbox
            text="Remember for 30 days"
            checked={rememberChecked}
            onChange={setRememberChecked}
          />
          <Button
            hierarchy="link"
            text="Forgot password"
            as={Link}
            to="/forgot-password"
          />
        </div>
        <Button type="submit" text="Login" disabled={isSubmitting} />
        <Divider
          className={styles.divider}
          text="OR"
          backgroundColor="#FBFCFF"
        />
        <Button
          type="button"
          hierarchy="secondary-outlined"
          text="Login with MetaMask"
          icon="metamask"
          onClick={handleLoginWithWeb3}
          disabled={isDisabled || isSubmitting}
        />
      </form>
    </div>
  );
}

export default Login;
